/* eslint-disable no-unreachable */
import styled from '@emotion/styled'
import { useTranslation } from '~/components/TranslationContext'
import { H2, Button, space, device, Avatar, useTransition, easing } from '@ticketswap/solar'
import { useLazyQuery } from '@apollo/client'
import TestimonialCard from '~/components/TestimonialCard'
import { GET_TESTIMONIALS } from '~/graphql/queries/getTestimonials'
import { GET_USERCOUNT_AND_AVATARS } from '~/graphql/queries/getUsercountAndAvatars'
import useLanguage from '~/hooks/useLanguage'
import { Block } from '~/components/Block'
import { WidePageLayout } from '~/components/PageLayout'
import useStats from '~/hooks/useStats'
import { useEffect, useState } from 'react'
import { css } from '@emotion/react'

const Center = styled.header`
  text-align: center;
  margin-inline-start: auto;
  margin-inline-end: auto;
  padding-inline-start: ${space[16]};
  padding-inline-end: ${space[16]};

  @media ${device.laptop} {
    max-width: ${600 / 16}rem;
  }
`

const Container = styled.div`
  position: relative;
`

const Cards = styled(WidePageLayout)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${384 / 16}rem;
`

const duration = 400
const ItemContainer = styled.div`
  position: relative;
  width: 80%;
  opacity: ${props => (props.state === 'ENTERING' || props.state === 'ENTERED' ? 1 : 0)};
  transition-timing-function: ${easing.easeOutCubic};
  transition-property: opacity, transform;

  &:first-of-type {
    transform: ${props => {
      switch (props.state) {
        case 'EXITED':
          return 'rotate(4deg) translate3d(2rem,0,0)'
        case 'EXITING':
          return 'rotate(4deg) translate3d(-2rem,0,0)'
        default:
          return 'rotate(4deg) translate3d(0,0,0)'
      }
    }};
    transition-duration: ${duration - 300}ms;
    display: none;
  }

  &:nth-of-type(2) {
    transform: ${props => {
      switch (props.state) {
        case 'EXITED':
          return 'rotate(-3deg) translate3d(2rem,0,0)'
        case 'EXITING':
          return 'rotate(-3deg) translate3d(-2rem,0,0)'
        default:
          return 'rotate(-3deg) translate3d(0,0,0)'
      }
    }};
    transition-duration: ${duration - 150}ms;
    display: none;
  }

  &:last-of-type {
    transform: ${props => {
      switch (props.state) {
        case 'EXITED':
          return 'rotate(4deg) translate3d(2rem,0,0)'
        case 'EXITING':
          return 'rotate(4deg) translate3d(-2rem,0,0)'
        default:
          return 'rotate(4deg) translate3d(0,0,0)'
      }
    }};
    transition-duration: ${duration}ms;
    display: block;
  }

  @media ${device.laptop} {
    width: 40%;

    &:first-of-type {
      display: block;
    }
  }

  @media ${device.laptop} {
    width: calc(100% / 3);

    &:nth-of-type(2) {
      display: block;
    }
  }
`

const Background = styled.div`
  position: absolute;
  inset: 0;
  padding-block-start: ${space[32]};
  padding-block-end: ${space[32]};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 80%);

    [data-theme='dark'] & {
      background-image: radial-gradient(rgb(18 20 22) 0%, rgb(0 0 0 / 0%) 50%);
    }

    [data-theme='ade'] & {
      background-image: radial-gradient(rgb(0, 0, 0) 0%, rgb(0 0 0 / 0%) 50%);
    }
  }
`

const Track = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.avatarsPerRow}, 1fr);
  grid-gap: ${space[16]};
`

const AvatarContainer = styled.div`
  text-align: center;
  width: ${space[128]};
  flex-shrink: 0;

  ${props =>
    props.isDisplayedInSecondRow &&
    css`
      position: relative;
      inset-inline-start: -${space[64]};
    `}
`

const Testimonials = () => {
  const { t } = useTranslation()
  let timer = null
  let language = useLanguage()
  const { countriesCount } = useStats({ t })
  const [fetchingMore, setFetchingMore] = useState(false)

  if (language === 'pt-BR') {
    // Workaround: API has no support for locales. For us pt-br is not pt, but for backend it is.
    language = 'pt'
  }

  const queryOptions = {
    notifyOnNetworkStatusChange: true,
    variables: {
      first: 3,
      language,
      minimumRating: 4,
      minimumLength: 5,
      maximumLength: 200,
    },
  }
  const [getTestimonials, { loading, data = {}, fetchMore }] = useLazyQuery(
    GET_TESTIMONIALS,
    queryOptions
  )
  const [getUsercountAndAvatars, { data: usercountAndAvatarsData = {} }] =
    useLazyQuery(GET_USERCOUNT_AND_AVATARS)

  useEffect(() => {
    getTestimonials()
    getUsercountAndAvatars()
    return () => {
      clearTimeout(timer)
    }
  }, [getTestimonials, getUsercountAndAvatars, timer])

  const { testimonials = {} } = data
  const { randomUserAvatars = [], userCount } = usercountAndAvatarsData
  const { edges = [], pageInfo = {} } = testimonials

  const [state, mounted] = useTransition({
    in: !loading && !fetchingMore && edges.length > 0,
    timeout: duration,
  })

  const handleFetchMore = () => {
    setFetchingMore(true)
    timer = setTimeout(async () => {
      await fetchMore({
        query: GET_TESTIMONIALS,
        variables: { ...queryOptions.variables, after: pageInfo.endCursor },
      })
      setFetchingMore(false)
    }, duration)
  }

  const avatarsPerRow = Math.ceil(randomUserAvatars.length / 3)

  if (edges?.length < 1) return null

  return (
    <Block>
      {userCount ? (
        <Center as="header">
          <H2>
            {t('common:testimonials.title', {
              count: new Intl.NumberFormat(language).format(userCount),
              countries: countriesCount,
            })}
          </H2>
        </Center>
      ) : null}

      <Container>
        {randomUserAvatars.length > 0 && (
          <Background>
            <Track avatarsPerRow={avatarsPerRow}>
              {randomUserAvatars.map((item, i) => (
                <AvatarContainer
                  key={item.avatar + i}
                  isDisplayedInSecondRow={i >= avatarsPerRow && i < avatarsPerRow * 2}
                >
                  <Avatar src={item.avatar} size={64} />
                </AvatarContainer>
              ))}
            </Track>
          </Background>
        )}

        {edges?.length > 0 && (
          <Cards>
            {edges.map(({ node }) =>
              mounted ? (
                <ItemContainer state={state} key={node.id}>
                  <TestimonialCard {...node} />
                </ItemContainer>
              ) : null
            )}
          </Cards>
        )}
      </Container>

      {pageInfo.hasNextPage && (
        <Center as="footer">
          <Button
            variant="secondary"
            size="medium"
            rounded
            onClick={handleFetchMore}
            loading={loading || fetchingMore}
            disabled={loading || fetchingMore}
          >
            {t('common:generic.showMore')}
          </Button>
        </Center>
      )}
    </Block>
  )
}

export default Testimonials
